import { get, set } from 'idb-keyval'

/**
 * The queue of orders taken while offline, and the operations needed to replay
 * them on reconnect.
 *
 * Lives in IndexedDB rather than localStorage for the same reason the query
 * cache does (see lib/idbPersister.ts): localStorage's ~5 MB quota fails
 * silently, and silent failure here means a lost sale.
 *
 * Every operation is APPEND-ONLY — create an order, add items to one. Nothing
 * here mutates or deletes server state, which is what collapses the entire
 * conflict surface to a single question at replay time: did this order reach a
 * terminal state while the terminal was dark?
 */

const STORE_KEY = 'bhookly-offline-orders-v1'

export interface OfflineLine {
  productId: string
  quantity: number
  special_instructions?: string
}

export interface OfflineOrderDraft {
  /** Reserved before the outage — already quoted to the customer. */
  orderNumber: string
  /** Epoch ms when the cashier committed the order. Drives business_day_id. */
  capturedAt: number
  orderType: string
  lines: OfflineLine[]
  userId: string
  /** Set when adding to an order that already exists on the server. */
  targetOrderId?: string
  /** Preserved on rehydrate so a replay retry reuses the same key. */
  opId?: string
}

export type OfflineOrderStatus = 'pending' | 'synced' | 'unreconciled'

export interface OfflineOrder extends OfflineOrderDraft {
  opId: string
  kind: 'create' | 'add_items'
  status: OfflineOrderStatus
  attempts: number
  lastError: string | null
}

/** Distinct, stable once assigned, and readable in a log line. */
export function nextOpId(userId: string, seq: number): string {
  return `offline:${userId}:${seq}:${Math.random().toString(36).slice(2, 10)}`
}

/**
 * Build a queue entry from a draft.
 *
 * Lines are rebuilt field-by-field rather than spread, so a caller that hands
 * over a richer object (a cart line carrying the whole Product, say) cannot
 * accidentally persist a base64 image or a stale price.
 */
export function makeOfflineOrder(d: OfflineOrderDraft): OfflineOrder {
  return {
    orderNumber: d.orderNumber,
    capturedAt: d.capturedAt,
    orderType: d.orderType,
    userId: d.userId,
    ...(d.targetOrderId ? { targetOrderId: d.targetOrderId } : {}),
    opId: d.opId ?? nextOpId(d.userId, d.capturedAt),
    kind: d.targetOrderId ? 'add_items' : 'create',
    status: 'pending',
    attempts: 0,
    lastError: null,
    lines: d.lines.map((l) => ({
      productId: l.productId,
      quantity: l.quantity,
      ...(l.special_instructions ? { special_instructions: l.special_instructions } : {}),
    })),
  }
}

/**
 * Error codes that will NEVER succeed on retry, so the operation must stop
 * being retried and go to the reconciliation queue for a human decision.
 *
 * `day_not_open` is deliberately absent: a manager can still open the day, at
 * which point parked orders flush in. Anything unrecognised is also absent, so
 * an unknown failure retries rather than silently discarding a sale the
 * customer has already walked out with.
 */
const TERMINAL_REPLAY_ERRORS = new Set([
  'order_not_editable',
  'order_not_found',
  'offline_order_duplicate',
])

export function isTerminalReplayError(code: string | null): boolean {
  return code != null && TERMINAL_REPLAY_ERRORS.has(code)
}

export async function listOfflineOrders(): Promise<OfflineOrder[]> {
  return (await get<OfflineOrder[]>(STORE_KEY)) ?? []
}

export async function enqueueOfflineOrder(d: OfflineOrderDraft): Promise<OfflineOrder> {
  const o = makeOfflineOrder(d)
  const all = await listOfflineOrders()
  await set(STORE_KEY, [...all, o])
  return o
}

export async function updateOfflineOrder(
  opId: string,
  patch: Partial<OfflineOrder>,
): Promise<void> {
  const all = await listOfflineOrders()
  await set(
    STORE_KEY,
    all.map((o) => (o.opId === opId ? { ...o, ...patch } : o)),
  )
}

/**
 * Append lines to an order that is still waiting to sync (F-FE-N31).
 *
 * During an outage a customer may keep adding to a held sale. Without this the
 * cashier could take the first round and then nothing else for that sale until
 * the connection returned — every later round would have had to be rung as a
 * separate order, which splits one bill into several.
 *
 * Editing a queued order is safe in exactly one window: while it is still
 * `pending`. Once replay has posted it the server owns it, and the ordinary
 * add-items path — which has the idempotency guard (F-CTRL-N37) — is the only
 * correct way in. Appending to the local copy after that point would queue
 * items against an order that will never be replayed again, so they would
 * never be billed.
 *
 * Returns the updated entry, or null when the entry is gone or has left
 * `pending`. A null is NOT a no-op: the caller still holds items the cashier
 * typed, and must route them to the real order rather than drop them.
 *
 * Lines are appended, never merged into matching ones. A later round is its own
 * round — the same rows an online add-items call would have produced.
 */
export async function appendLinesToPendingOfflineOrder(
  opId: string,
  lines: OfflineLine[],
): Promise<OfflineOrder | null> {
  const all = await listOfflineOrders()
  const target = all.find((o) => o.opId === opId)
  if (!target || target.status !== 'pending') return null

  const updated: OfflineOrder = {
    ...target,
    // Rebuilt field-by-field for the same reason makeOfflineOrder does it: a
    // caller handing over a cart line must not be able to persist a Product.
    lines: [
      ...target.lines,
      ...lines.map((l) => ({
        productId: l.productId,
        quantity: l.quantity,
        ...(l.special_instructions ? { special_instructions: l.special_instructions } : {}),
      })),
    ],
  }
  await set(
    STORE_KEY,
    all.map((o) => (o.opId === opId ? updated : o)),
  )
  return updated
}

/**
 * Drop successfully-synced entries. Pending and unreconciled ones stay — an
 * unreconciled entry is unresolved work, and dropping it locally would hide
 * goods that were sold offline but never posted.
 */
export async function pruneSyncedOfflineOrders(): Promise<void> {
  const all = await listOfflineOrders()
  await set(
    STORE_KEY,
    all.filter((o) => o.status !== 'synced'),
  )
}

/** Entries still waiting to post, oldest first — a create must precede its add-items. */
export async function pendingOfflineOrders(): Promise<OfflineOrder[]> {
  return (await listOfflineOrders())
    .filter((o) => o.status === 'pending')
    .sort((a, b) => a.capturedAt - b.capturedAt)
}
